/**
 * @fileoverview Build Your Case Wizard Container
 * @module BuildYourCaseWizard
 * 
 * Business Context:
 * - Main container for the Step 4 "Build Your Case" activity
 * - Guides users through a 4-phase wizard: Review → Confirm → Generate → Complete
 * - Consistent navigation pattern across all phases
 */

import React, { useState, useEffect, useCallback } from 'react';
import WizardProgressBar, { WizardPhase } from './WizardProgressBar';
import ReviewEvidencePhase from './phases/ReviewEvidencePhase';
import ConfirmReductionPhase from './phases/ConfirmReductionPhase';
import GeneratingPhase from './phases/GeneratingPhase';
import MeetingKitPhase from './phases/MeetingKitPhase';
import { apiClient } from '../../api/client';

export interface AppealSummary {
  propertyId: string;
  property: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    county: string;
    propertyType: string;
    yearBuilt: number | null;
    squareFootage: number | null;
  };
  maintenanceSummary: {
    totalIssues: number;
    totalEstimatedCost: number;
    overallSeverity: string;
    categories: {
      category: string;
      issueCount: number;
      estimatedCost: number;
      keyFindings: string[];
      notes: string | null;
      professionalAssessment: string | null;
    }[];
  } | null;
  flawSummary: {
    totalFlawsAnalyzed: number;
    totalIssuesFound: number;
    flaws: {
      flawType: string;
      flawLabel: string;
      issueCount: number;
      checkedItems: string[];
      notes: string | null;
    }[];
  } | null;
  reductionBreakdown: {
    // Three-tier reduction strategy
    opening: number;
    target: number;
    walkaway: number;
    // Impact breakdown
    maintenanceImpact: number;
    maintenanceDetails?: {
      totalRepairCost: number;
      baseImpact: number;
      adjustedImpact: number;
      categoryBreakdowns: any[];
    };
    flawImpact: number;
    flawDetails?: {
      wrongDetailsImpact: number;
      qualityMisclassificationImpact: number;
      conditionMisclassificationImpact: number;
      outdatedSalesImpact: number;
    };
    // Tax savings
    taxSavings: {
      opening: number;
      target: number;
      walkaway: number;
    };
    // Confidence
    confidence: 'low' | 'medium' | 'high';
    confidenceReasons: string[];
  } | null;
  generatedAt: string;
}

export interface GeneratedArgument {
  id: string;
  script: {
    openingLine: string;
    keyPoints: string[];
    closingLine: string;
    suggestedReduction: string;
  };
  tips: string[];
  generatedAt: string;
}

interface BuildYourCaseWizardProps {
  propertyId: string;
  existingArgument?: GeneratedArgument | null;
  onComplete: () => void;
  onBack: () => void;
}

const BuildYourCaseWizard: React.FC<BuildYourCaseWizardProps> = ({
  propertyId,
  existingArgument,
  onComplete,
  onBack
}) => {
  const [currentPhase, setCurrentPhase] = useState<WizardPhase>(
    existingArgument ? 'complete' : 'review'
  );
  const [summary, setSummary] = useState<AppealSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatedArgument, setGeneratedArgument] = useState<GeneratedArgument | null>(
    existingArgument || null
  );
  const [confirmedReduction, setConfirmedReduction] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.argument.getAppealSummary(propertyId);
        if (response.data.success) {
          setSummary(response.data.data);
          // Use target from reduction breakdown
          const reduction = response.data.data.reductionBreakdown?.target ?? 0;
          if (reduction > 0) {
            setConfirmedReduction(reduction);
          }
        } else {
          throw new Error('Failed to load evidence summary');
        }
      } catch (err: any) {
        console.error('Error fetching appeal summary:', err);
        setError(err.message || 'Failed to load your evidence');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [propertyId]);

  const goToNextPhase = useCallback(() => {
    switch (currentPhase) {
      case 'review':
        setCurrentPhase('confirm');
        break;
      case 'confirm':
        setCurrentPhase('generate');
        break;
      case 'generate':
        setCurrentPhase('complete');
        break;
      case 'complete':
        onComplete();
        break;
    }
  }, [currentPhase, onComplete]);

  const goToPreviousPhase = useCallback(() => {
    switch (currentPhase) {
      case 'confirm':
        setCurrentPhase('review');
        break;
      case 'generate':
        setCurrentPhase('confirm');
        break;
      case 'complete':
        setCurrentPhase('confirm');
        break;
      case 'review':
        onBack();
        break;
    }
  }, [currentPhase, onBack]);

  const handleGenerate = useCallback(async () => {
    try {
      setIsGenerating(true);
      setCurrentPhase('generate');
      
      const response = await apiClient.argument.generate(propertyId);
      
      if (response.data.success) {
        setGeneratedArgument(response.data.data);
        setTimeout(() => {
          setCurrentPhase('complete');
          setIsGenerating(false);
        }, 500);
      } else {
        throw new Error(response.data.error?.message || 'Failed to generate script');
      }
    } catch (err: any) {
      console.error('Error generating argument:', err);
      setError(err.message || 'Failed to generate your script');
      setCurrentPhase('confirm');
      setIsGenerating(false);
    }
  }, [propertyId]);

  const handleRegenerate = useCallback(async () => {
    setGeneratedArgument(null);
    await handleGenerate();
  }, [handleGenerate]);

  const hasEvidence = summary && (
    (summary.maintenanceSummary && summary.maintenanceSummary.totalIssues > 0) ||
    (summary.flawSummary && summary.flawSummary.totalIssuesFound > 0)
  );

  const renderPhaseContent = () => {
    if (loading) {
      return (
        <div className="bg-white rounded-2xl border border-slate-200 p-16">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-500">Loading your evidence...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-xl">!</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h3>
          <p className="text-slate-500 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    switch (currentPhase) {
      case 'review':
        return (
          <ReviewEvidencePhase
            summary={summary}
            hasEvidence={!!hasEvidence}
            onContinue={goToNextPhase}
          />
        );

      case 'confirm':
        return (
          <ConfirmReductionPhase
            summary={summary}
            confirmedReduction={confirmedReduction}
            onReductionChange={setConfirmedReduction}
            onGenerate={handleGenerate}
            onBack={goToPreviousPhase}
          />
        );

      case 'generate':
        return (
          <GeneratingPhase isGenerating={isGenerating} />
        );

      case 'complete':
        return (
          <MeetingKitPhase
            argument={generatedArgument}
            summary={summary}
            propertyId={propertyId}
            onRegenerate={handleRegenerate}
            isRegenerating={isGenerating}
            onViewEvidence={() => setCurrentPhase('review')}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      {/* Progress Bar */}
      <WizardProgressBar currentPhase={currentPhase} />

      {/* Phase Content */}
      {renderPhaseContent()}
    </div>
  );
};

export default BuildYourCaseWizard;
